import { FC, Fragment, useState, useEffect, useRef, useContext } from "react";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { AiResponseReviewService } from "@app/operations/ai-response-review/aiResponseReviewService";
import {
  AiResponseReviewResult,
  AiResponseReviewUpdateInternalModel,
  AiChatMessageType
} from "@app/operations/ai-response-review/interfaces";
import { AiResponseReviewRating } from "@app/operations/ai-response-review/enums";

import { formatRating } from "@app/operations/ai-response-review/formatRating";
import { formatDateTime, formatUnconvertedDateCustom } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { FlyoutContext } from "@app/shared/Flyouts/FlyoutContext";

interface AiResponseDetailPanelProps {
  responseId: number;
  /**
   * When supplied, renders a "View Thread" button beside the Thread ID that filters
   * the dashboard down to this response's thread. Omitted when the flyout is rendered
   * outside the review table, where there is no filter state to drive.
   */
  onViewThread?: (threadId: string) => void;
  /**
   * Notifies the owner that this response was edited, so it can refresh its list once
   * the flyout closes. The panel deliberately does not refresh the list itself — doing
   * so while it is still open reorders the rows the flyout is anchored to.
   */
  onSaved?: () => void;
}

const AiResponseDetailPanel: FC<AiResponseDetailPanelProps> = ({ responseId, onViewThread, onSaved }) => {
  const { handleCloseFlyout } = useContext(FlyoutContext);
  const [detail, setDetail] = useState<AiResponseReviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [internalRating, setInternalRating] = useState<AiResponseReviewRating>(AiResponseReviewRating.None);
  const [internalReason, setInternalReason] = useState("");
  const [devResolved, setDevResolved] = useState(false);

  const service = useRef(new AiResponseReviewService()).current;

  const ratingOptions: DropdownItem<AiResponseReviewRating>[] = [
    { key: AiResponseReviewRating.None, name: "None" },
    { key: AiResponseReviewRating.Good, name: "Good" },
    { key: AiResponseReviewRating.Bad, name: "Bad" }
  ];

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await service.getAiResponseReview(responseId);
        setDetail(result);
        setInternalRating(result.internalRating || AiResponseReviewRating.None);
        setInternalReason(result.internalReason || "");
        setDevResolved(result.devResolved);
      } catch (err) {
        setError("Failed to load response details");
        console.error("Error loading AI response detail:", err);
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [responseId]);

  // Applies the filter before closing so the table can cancel any refresh owed from an
  // edit here; closing first would let its onClose fire that refresh redundantly.
  const handleViewThreadClick = () => {
    if (!onViewThread || !detail?.threadId) return;

    onViewThread(detail.threadId);
    handleCloseFlyout();
  };

  const handleSave = async () => {
    if (!detail) return;

    try {
      const update: AiResponseReviewUpdateInternalModel = {
        devResolved: devResolved,
        internalRating,
        internalReason
      };

      await service.updateInternalAiResponseReview(responseId, update);
      onSaved?.();
      setEditing(false);

      const updatedDetail = await service.getAiResponseReview(responseId);
      setDetail(updatedDetail);
    } catch (err) {
      console.error("Error updating AI response:", err);
    }
  };

  const handleCancel = () => {
    if (detail) {
      setInternalRating(detail.internalRating || AiResponseReviewRating.None);
      setInternalReason(detail.internalReason || "");
      setDevResolved(detail.devResolved)
    }

    setEditing(false);
  };

  if (loading) {
    return (
      <div className="flyout-panel">
        <div className="flyout-body text-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flyout-panel">
        <div className="flyout-body">
          <div className="alert alert-danger">{error || "Response not found"}</div>
        </div>
      </div>
    );
  }

  const promptVersions = [
    { label: "Chat History Reduction Prompt", value: detail.chatHistoryReductionPromptVersion },
    { label: "Request Type Prompt", value: detail.requestTypePromptVersion },
    { label: "Relevance Check Prompt", value: detail.relevanceCheckPromptVersion },
    { label: "Knowledge Check Prompt", value: detail.knowledgeCheckPromptVersion },
    { label: "License Check Prompt", value: detail.licenseCheckPromptVersion },
    { label: "Data Specialties Check Prompt", value: detail.dataSpecialtiesCheckPromptVersion },
    { label: "Security Check Prompt", value: detail.securityCheckPromptVersion },
    { label: "Cortex Prompt", value: detail.cortexPromptVersion },
    { label: "Cortex Data Prompt", value: detail.cortexDataPromptVersion },
    { label: "Cortex Suggestions Prompt", value: detail.cortexSuggestionsPromptVersion },
    { label: "Response Type Prompt", value: detail.responseTypePromptVersion },
    { label: "Reformat Prompt", value: detail.reformatPromptVersion },
    { label: "Explanation Prompt", value: detail.explanationPromptVersion },
    { label: "Limit Check Prompt", value: detail.limitCheckPromptVersion },
    { label: "Clarification Prompt", value: detail.clarificationPromptVersion },
    { label: "Semantic View Router Prompt", value: detail.semanticViewRouterPromptVersion },
    { label: "Servicing Provider Schema Prompt", value: detail.servicingProviderSchemaPromptVersion },
    { label: "Contract Aggregates Schema Prompt", value: detail.contractAggregatesSchemaPromptVersion },
    { label: "Adequacy Results Schema Prompt", value: detail.adequacyResultsSchemaPromptVersion },
    { label: "Generator Prompt", value: detail.generatorPromptVersion },
    { label: "Chart Header Prompt", value: detail.chartHeaderPromptVersion },
    { label: "How I Found This Prompt", value: detail.howIFoundPromptVersion },
  ].filter(p => p.value);

  return (
    <div className="flyout-panel">
      <div className="flyout-body">
        <div className="row">
          <div className="col-12">
            <dl className="row">
              <dt className="col-sm-3">Date/Time (Local)</dt>
              <dd className="col-sm-9">{formatDateTime(detail.createdUtc)}</dd>

              <dt className="col-sm-3">Date/Time (UTC)</dt>
              <dd className="col-sm-9">{formatUnconvertedDateCustom(detail.createdUtc, "LL LT")}</dd>

              <dt className="col-sm-3">Thread ID</dt>
              <dd className="col-sm-9">
                {detail.threadId}
                {!!onViewThread && !!detail.threadId && (
                  <QESButton
                    qesType="link"
                    qesTestId="ai-response-review__view-thread"
                    className="ms-2"
                    onClick={handleViewThreadClick}
                  >
                    View Thread
                  </QESButton>
                )}
              </dd>

              <dt className="col-sm-3">Client ID</dt>
              <dd className="col-sm-9">{detail.clientId} - {detail.clientName}</dd>

              <dt className="col-sm-3">Project ID</dt>
              <dd className="col-sm-9">{detail.projectId} - {detail.projectName}</dd>

              <dt className="col-sm-3">Environment</dt>
              <dd className="col-sm-9">{detail.environment}</dd>

              <dt className="col-sm-3">User ID</dt>
              <dd className="col-sm-9">{detail.userId} - {detail.userName}</dd>

              <dt className="col-sm-3">Search Service Max Definitions</dt>
              <dd className="col-sm-9">{detail.searchServiceMaximumDefinitions ?? "-"}</dd>

              <dt className="col-sm-3">Search Service Score Cutoff</dt>
              <dd className="col-sm-9">{detail.searchServiceScoreCutoff ?? "-"}</dd>

              <dt className="col-sm-3">Semantic Kernel Temperature</dt>
              <dd className="col-sm-9">{detail.semanticKernelTemperature ?? "-"}</dd>

              <dt className="col-sm-3">Response Time</dt>
              <dd className="col-sm-9">{detail.responseTimeMs}ms</dd>

              <dt className="col-sm-3">User Rating</dt>
              <dd className="col-sm-9">{formatRating(detail.userRating)}</dd>

              <dt className="col-sm-3">User Reason</dt>
              <dd className="col-sm-9">{detail.userReason || "-"}</dd>

              <dt className="col-sm-3">Message Type</dt>
              <dd className="col-sm-9">{detail.messageType || "-"}</dd>

              {detail.messageType === AiChatMessageType.Table && (
                <>
                  <dt className="col-sm-3">Table Downloaded</dt>
                  <dd className="col-sm-9">{detail.tableDownloaded === true ? "Y" : detail.tableDownloaded === false ? "N" : "-"}</dd>
                </>
              )}

              {!!detail.chartHeader && (
                <>
                  <dt className="col-sm-3">Chart Header</dt>
                  <dd className="col-sm-9">{detail.chartHeader}</dd>
                </>
              )}

              {!!detail.howIFound && (
                <>
                  <dt className="col-sm-3">How I Found This</dt>
                  <dd className="col-sm-9">{detail.howIFound}</dd>
                </>
              )}

              <dt className="col-sm-3">Previous SQL Loaded</dt>
              <dd className="col-sm-9">{detail.previousSqlLoaded === true ? "Y" : detail.previousSqlLoaded === false ? "N" : "-"}</dd>

              {promptVersions.length > 0 && (
                <>
                  <dt className="col-sm-12"><h6 className="mt-3">Prompt Versions</h6></dt>
                  {promptVersions.map(p => (
                    <Fragment key={p.label}>
                      <dt className="col-sm-3">{p.label}</dt>
                      <dd className="col-sm-9">{p.value}</dd>
                    </Fragment>
                  ))}
                </>
              )}
            </dl>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-12">
            <h5>Prompt</h5>
            <div className="border p-2" style={{ maxHeight: "200px", overflow: "auto", whiteSpace: "pre-wrap" }}>
              {detail.prompt}
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-12">
            <h5>Response</h5>
            <div className="border p-2" style={{ maxHeight: "200px", overflow: "auto", whiteSpace: "pre-wrap" }}>
              {detail.response}
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <h5>Internal Review</h5>
            {editing ? (
              <>
                <div className="form-group row">
                  <QESSelect
                    id={"ai-response-review__internal-rating-dropdown"}
                    containerClassName="mt-4 col-3"
                    defaultValue={AiResponseReviewRating.None}
                    label="Internal Rating"
                    onChange={(e) => setInternalRating(e.target.value as AiResponseReviewRating || AiResponseReviewRating.Unknown)}
                    testId="ai-response-review__internal-rating-dropdown"
                    value={internalRating}
                  >
                    {ratingOptions.map(option => (
                      <option key={option.key} value={option.key}>
                        {option.name == 'None' ? '' : option.name}
                      </option>
                    ))}
                  </QESSelect>
                </div>
                <div className="form-group mb-0">
                  <QESTextArea
                    id={"ai-response-review__internal-reason-input"}
                    containerClassName="mt-4"
                    rows={3}
                    value={internalReason}
                    onChange={(e) => setInternalReason(e.target.value)}
                    maxLength={4000}
                    label="Internal Reason"
                    testId="ai-response-review__internal-reason-input"
                  />
                </div>
                <div className="form-group">
                  <QESCheckbox
                    containerClassName="mt-0"
                    testId="ai-response-review__dev-resolved-input"
                    label="Dev Resolved"
                    checked={devResolved}
                    onChange={(e) => setDevResolved(e.target.checked)}
                  />
                </div>
                <div className="form-group">
                  <QESButton qesType="primary" qesTestId="ai-response-review__save-internal-review"
                    onClick={handleSave}>
                    Save
                  </QESButton>
                  <QESButton qesType="secondary" qesTestId="ai-response-review__cancel-internal-review"
                    onClick={handleCancel} className="ms-2">
                    Cancel
                  </QESButton>
                </div>
              </>
            ) : (
              <>
                <dl className="row">
                  <dt className="col-sm-3">Internal Rating</dt>
                  <dd className="col-sm-9">{formatRating(detail.internalRating)}</dd>

                  <dt className="col-sm-3">Internal Reason</dt>
                  <dd className="col-sm-9">{detail.internalReason || "-"}</dd>

                  <dt className="col-sm-3">Last Updated</dt>
                  <dd className="col-sm-9">
                    {detail.internalLastUpdatedUtc ? formatDateTime(detail.internalLastUpdatedUtc) : "-"}
                  </dd>

                  <dt className="col-sm-3">Updated By User ID</dt>
                  <dd className="col-sm-9">{detail.internalUpdatedByUserId ? `${detail.internalUpdatedByUserId} - ${detail.internalUpdatedByUserName}` : "-"}</dd>

                  <dt className="col-sm-3">Dev Resolved</dt>
                  <dd className="col-sm-9">{detail.devResolved ? "Yes" : "No"}</dd>
                </dl>
                <QESButton qesType="outline" qesTestId="ai-response-review__edit-internal-review" onClick={() => setEditing(true)}>
                  Edit Internal Review
                </QESButton>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiResponseDetailPanel;